// src/components/EditTermModal/EditTermModal.tsx
import React, {
  FC, useState, useCallback, useEffect, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input, Button, Form } from 'antd';
import { saveWordList } from '../../views/words-list/slice/actions';
import { MAX_WORD_FIELD_LENGTH } from '../../views/words-list/WordsList.schema';
import { getActiveList } from '../../views/auth/slice/selectors';
import { setActiveList } from '../../views/auth/slice/actions';
import { error } from '../Message';
import AppModal, { ModalButtons } from '../AppModal';
import styles from '../../views/words-lists/FolderTreeView.module.less';
import { fetchFolderTreeWithLists } from '../../views/folders/slice/actions';
import ApiService from '../../services/ApiService';

interface EditTermModalProps {
  visible: boolean;
  wordId: string;
  currentTerm: string;
  currentDefinition: string;
  listId: string;
  onClose: () => void;
  onSuccess?: () => void;
  onTermUpdated?: (newTerm: string, newDefinition: string) => void;
}

export const EditTermModal: FC<EditTermModalProps> = ({
  visible,
  wordId,
  currentTerm,
  currentDefinition,
  listId,
  onClose,
  onSuccess,
  onTermUpdated,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const activeList = useSelector(getActiveList);

  const isMountedRef = useRef(true);

  const [term, setTerm] = useState(currentTerm || '');
  const [definition, setDefinition] = useState(currentDefinition || '');
  const [isSaving, setIsSaving] = useState(false);

  // ✅ Cleanup function
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (visible) {
      setTerm(currentTerm || '');
      setDefinition(currentDefinition || '');
    }
  }, [visible, currentTerm, currentDefinition]);

  const handleTermChange = useCallback((value: string) => {
    setTerm(value);
  }, []);

  const handleDefinitionChange = useCallback((value: string) => {
    setDefinition(value);
  }, []);

  // W trakcie zapisu okno się nie zamyka — ani „Anuluj”, ani krzyżyk, ani Esc
  const handleCancel = useCallback(() => {
    if (isSaving) {
      return;
    }

    setTerm('');
    setDefinition('');
    onClose();
  }, [isSaving, onClose]);

  const handleSubmit = useCallback(async () => {
    const trimmedTerm = term?.trim() || '';

    if (!trimmedTerm) {
      error(t('words-list:editTerm.messages.emptyTerm'));

      return;
    }

    if (!activeList || !activeList.words) {
      error(t('words-list:editTerm.messages.cannotUpdate'));

      return;
    }

    // Tylko pola zmienione w oknie. Niezmienione zostają w wersji z serwera, bo tę mogła
    // w międzyczasie poprawić inna karta albo inne urządzenie.
    const changes: { term?: string, definition?: string } = {};

    if (term !== (currentTerm || '')) {
      changes.term = trimmedTerm;
    }
    if (definition !== (currentDefinition || '')) {
      changes.definition = (definition || '').trim();
    }

    // Nic nie zmieniono: bez zapisu, żeby lista nie wskakiwała na górę u nauczyciela
    if (Object.keys(changes).length === 0) {
      onClose();

      return;
    }

    setIsSaving(true);

    try {
      const applyEdit = (words: any[]) => words.map((word: any) => (
        word.id === wordId ? { ...word, ...changes } : word
      ));

      // Zapis nadpisuje na serwerze całą listę, a kopia w grze bywa sprzed zmian zrobionych gdzie
      // indziej (nazwa, opis, słowa). Dlatego poprawkę nakładamy na listę pobraną tuż przed zapisem.
      const { data: currentList } = await ApiService.wordsList.getOne(listId);

      if (!currentList.words.some((word: any) => word.id === wordId)) {
        error(t('words-list:editTerm.messages.wordRemoved'));
        onClose();

        return;
      }

      const updatedValues = {
        name: currentList.name,
        description: (currentList as any).description,
        words: applyEdit(currentList.words),
      };

      await dispatch(saveWordList(listId, updatedValues, true));

      // ✅ Po prostu odśwież drzewo
      await dispatch(fetchFolderTreeWithLists(true) as any);

      // Gra zmienia u siebie tylko poprawione słowo, bez zmian zrobionych gdzie indziej
      const updatedActiveList = { ...activeList, words: applyEdit(activeList.words) };
      dispatch(setActiveList(updatedActiveList));

      onTermUpdated?.(trimmedTerm, (definition || '').trim());
      onSuccess?.();

      setTerm('');
      setDefinition('');
      onClose();
    } catch (err) {
      console.error('❌ EDIT TERM MODAL - Błąd podczas aktualizacji:', err);
      if (isMountedRef.current) {
        setIsSaving(false);
      }
      error(t('words-list:editTerm.messages.error'));
    }
  }, [term, definition, currentTerm, currentDefinition, wordId, activeList, listId, dispatch, onClose, onSuccess,
    onTermUpdated, t]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Szersze niż zwykłe okienko — zwroty i definicje bywają długie
  return (
    <AppModal title={t('words-list:editTerm.title')} visible={visible} onCancel={handleCancel} wide>
      <Form layout="vertical" className={styles.listModalForm}>
        <Form.Item
          required
          className={styles.listModalFormItem}
        >
          <Input
            placeholder={t('words-list:editTerm.termPlaceholder')}
            value={term}
            onChange={(e) => handleTermChange(e.target.value)}
            onPressEnter={handleKeyPress}
            maxLength={MAX_WORD_FIELD_LENGTH}
            autoFocus
            disabled={isSaving}
            className={styles.listModalInput}
          />
        </Form.Item>

        <Form.Item
          className={styles.listModalFormItem}
        >
          <Input
            placeholder={t('words-list:editTerm.definitionPlaceholder')}
            value={definition}
            onChange={(e) => handleDefinitionChange(e.target.value)}
            onPressEnter={handleKeyPress}
            maxLength={MAX_WORD_FIELD_LENGTH}
            disabled={isSaving}
            className={styles.listModalInput}
          />
        </Form.Item>
      </Form>

      <ModalButtons>
        <Button
          onClick={handleCancel}
          disabled={isSaving}
        >
          {t('common:buttons.cancel')}
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={isSaving}
          disabled={!term?.trim()}
        >
          {t('common:buttons.save')}
        </Button>
      </ModalButtons>
    </AppModal>
  );
};
